import type {
  SummaryDiagnosticGroupOption,
  SummaryTarget,
  SummaryTargetCatalog,
} from "@/api/summaryTargets";

/**
 * How a Summary target is READ on screen (#863).
 *
 * ## THE ONE RULE THIS MODULE HOLDS
 *
 * A target is an IDENTITY the service owns, and this module never constructs
 * one from parts a clinician typed. It turns a target the server sent — on an
 * asset, or as an option in the catalog — into the words and the sorting a
 * surface needs, and it turns a clinician's SELECTION back into the same typed
 * object the create route takes. Nothing here builds a path: the coordinate a
 * row shows is `asset.placementCoordinate`, served, and the directory a picker
 * shows is the catalog option's own `directory`, expanded by the server.
 *
 * ## WHY THE SLUG IS THE NAME
 *
 * `background_sections` is not humanised to "Background sections" anywhere a
 * target is IDENTIFIED. The slug is the clinical identity — it is the directory
 * the pinned engine opens and the word the pack, the runbook and the Lovable
 * reference all use — and a prettified label would be a second name for one
 * thing, which is how a clinician comes to believe they authored a different
 * target from the one they did. The reference lists the slugs verbatim, in a
 * monospace column, and so does this application.
 */

/** The slug pair a diagnostic target is, joined for display and for keys. */
export function diagnosticTargetName(group: string, evaluation: string): string {
  return `${group}/${evaluation}`;
}

/**
 * The identity a clinician reads a target by — the slug, or `group/evaluation`.
 *
 * Total over the union rather than defaulting: a third `kind` in a regenerated
 * contract must fail to compile here rather than render as a section.
 */
export function summaryTargetName(target: SummaryTarget): string {
  return target.kind === "section"
    ? target.section
    : diagnosticTargetName(target.group, target.evaluation);
}

/**
 * Which of the two shapes a target is, as the word a heading uses.
 *
 * Deliberately not "flat"/"nested": those describe the coordinate's arity, which
 * is an implementation fact. A clinician selects between a report SECTION and
 * one evaluation's DIAGNOSTIC FINDINGS, and the contract's own §6 names them
 * that way.
 */
export const SUMMARY_TARGET_KIND_LABELS = {
  section: "Section",
  diagnostic: "Diagnostic finding",
} as const satisfies Record<SummaryTarget["kind"], string>;

/**
 * A stable key for a target, for React lists and for occupancy lookups.
 *
 * Prefixed by kind so a section named `depression` — which the flat catalog
 * does not contain today, but which is not a rule this function may assume —
 * can never collide with the `depression/depression` diagnostic one.
 */
export function summaryTargetKey(target: SummaryTarget): string {
  return `${target.kind}:${summaryTargetName(target)}`;
}

/** The same key for a catalog option a clinician has not selected yet. */
export function sectionTargetKey(section: string): string {
  return `section:${section}`;
}

export function diagnosticTargetKey(group: string, evaluation: string): string {
  return `diagnostic:${diagnosticTargetName(group, evaluation)}`;
}

/**
 * The evaluations of one group, or an empty list when the group is unknown.
 *
 * `undefined` for the catalog rather than a required argument: every surface
 * that renders a dependent Evaluation control has to behave while the catalog
 * read is still in flight, and an unknown group is the same answer as no
 * catalog — offer nothing rather than offer something unverified.
 */
export function evaluationsOfGroup(
  catalog: SummaryTargetCatalog | undefined,
  group: string,
): SummaryDiagnosticGroupOption["evaluations"] {
  return (
    catalog?.diagnosticGroups.find((candidate) => candidate.group === group)
      ?.evaluations ?? []
  );
}

/**
 * Who currently holds a target, and on what terms.
 *
 * `seeded` is the whole reason this is a record rather than a name: the two
 * kinds of holder mean OPPOSITE things to a create.
 *
 *   authored — `uq_library_assets_active_placement` is a partial unique index on
 *              the STORED coordinate, so a second live authored row at one
 *              target is refused 409. The option must not be offered.
 *   seeded   — base canon stores no coordinate at all; the one it reports is
 *              derived from the seed manifest, so the index does not cover it
 *              and the service ACCEPTS an authored asset there. That is not a
 *              loophole, it is the documented way a project replaces base
 *              canon's prompt — the seeded row's own refusal says "copy it into
 *              a new asset". Disabling it would remove the journey.
 */
export type SummaryTargetHolder = { name: string; seeded: boolean };

/**
 * Every target the ACTIVE library already holds, by key.
 *
 * WHY A CLIENT MAY DERIVE THIS AND MUST NOT DERIVE THE VOCABULARY. Occupancy is
 * not service policy — it is what the catalog read already says, one
 * `summaryTarget` per asset — so computing it here reads data in hand rather
 * than restating a rule. The SERVER still decides; this only keeps a picker
 * from offering the option that could not be accepted.
 *
 * Archived assets are excluded because archiving FREES a target — that is the
 * documented correction for a wrong immutable placement — so a picker that
 * treated an archived row as a holder would refuse the one remedy the service
 * offers.
 */
export function occupiedSummaryTargets(
  assets: readonly {
    summaryTarget?: SummaryTarget | null;
    name: string;
    capabilities: { archived: boolean; seeded: boolean };
  }[],
): Map<string, SummaryTargetHolder> {
  const held = new Map<string, SummaryTargetHolder>();
  for (const asset of assets) {
    if (!asset.summaryTarget || asset.capabilities.archived) {
      continue;
    }
    const key = summaryTargetKey(asset.summaryTarget);
    // An AUTHORED holder wins over a seeded one at the same target: it is the
    // one that refuses the write, and a target held by both is exactly the
    // replacement the seeded note describes having already been made.
    if (!asset.capabilities.seeded || !held.has(key)) {
      held.set(key, { name: asset.name, seeded: asset.capabilities.seeded });
    }
  }
  return held;
}
